import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Stack, Button, Text, Overlay, ControlLink, LoadingBalls } from '@moda/om';
import { throwQueryError } from '../../../lib/apollo/throwQueryError';
import { CVV_ERROR, PaymentSource } from '../../../constants';
import {
  PaymentMethod,
  PaymentMethodAttributes,
  usePaymentMethodsQuery
} from '../../../hooks/usePaymentMethods';
import { usePaymentError } from '../../../hooks/useCheckout/usePaymentError';
import { FormButtonsLayout } from '../../FormButtonsLayout';
import { EditableSection } from '../../EditableSection';
import { PaymentMethodForm } from '../PaymentMethodForm';
import { PaymentMethodOption } from '../PaymentMethodOption';
import { PaymentMethodPreview } from '../PaymentMethodPreview';
import { Loading } from '../../Loading';

import './AdyenPaymentMethodSelector.scss';

export enum Mode {
  Resting,
  Saving,
  Editing,
  Creating,
  Selecting
}

interface Props {
  description?: string;
  isChangeable?: boolean;
  defaultMode?: Mode;
  defaultPaymentMethodId?: number | null;
  defaultPaymentMethod?: PaymentMethodAttributes;
  onModeChange?: (incomingMode: number) => void;
  onSubmit: (selectedPaymentMethodId: string | null) => Promise<void>;
}

export const AdyenPaymentMethodSelector: React.FC<Props> = ({
  onSubmit,
  description,
  defaultMode = Mode.Resting,
  onModeChange,
  isChangeable,
  defaultPaymentMethodId,
  defaultPaymentMethod
}) => {
  const { paymentError, setPaymentError } = usePaymentError();

  const [mode, setMode] = useState(defaultMode);
  const [editingPaymentMethodId, setEditingPaymentMethodId] = useState<string | null>(null);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | null>(null);
  const { data: paymentMethodsData, error: paymentMethodsError } = usePaymentMethodsQuery();

  const paymentMethods = useMemo(
    () =>
      paymentMethodsData?.paymentMethods.data.filter(
        (method: PaymentMethod) => method.attributes.source === PaymentSource.Adyen
      ) || [],
    [paymentMethodsData]
  );
  const defaultPaymentMethodAttributes =
    defaultPaymentMethod ??
    paymentMethods.find((method: PaymentMethod) => method.id === String(defaultPaymentMethodId))
      ?.attributes;

  const handlePaymentMethodChange = useCallback(() => {
    setMode(Mode.Selecting);
  }, []);

  const handleContinue = useCallback(
    async (id: number | string) => {
      await onSubmit(String(id));
      setSelectedPaymentMethodId(String(id));
      setMode(Mode.Resting);
    },
    [onSubmit]
  );

  useEffect(() => {
    if (
      paymentMethodsData &&
      paymentMethods.length === 0 &&
      ((!defaultPaymentMethodAttributes && mode === Mode.Resting) || mode === Mode.Selecting)
    ) {
      setMode(Mode.Creating);
    } else if (!defaultPaymentMethodAttributes && mode === Mode.Resting) {
      setMode(Mode.Selecting);
    }
  }, [mode, setMode, paymentMethods, paymentMethodsData, defaultPaymentMethodAttributes]);

  useEffect(() => {
    if (defaultPaymentMethodId) setSelectedPaymentMethodId(String(defaultPaymentMethodId));
  }, [defaultPaymentMethodId, setSelectedPaymentMethodId]);

  useEffect(() => {
    if (paymentError === CVV_ERROR && mode === Mode.Resting && defaultPaymentMethodId) {
      setMode(Mode.Editing);
      setEditingPaymentMethodId(String(defaultPaymentMethodId));
    }
  }, [paymentError, mode, setMode, setEditingPaymentMethodId, defaultPaymentMethodId]);

  useEffect(() => {
    onModeChange?.(mode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  if (paymentMethodsError) throwQueryError(paymentMethodsError);

  if (!paymentMethodsData) return <Loading />;

  if (mode === Mode.Resting && defaultPaymentMethodAttributes)
    return (
      <EditableSection onChange={isChangeable ? handlePaymentMethodChange : undefined}>
        <Stack space={2}>
          <PaymentMethodPreview method={defaultPaymentMethodAttributes} />
          {description && <Text treatment="bold1">{description}</Text>}
        </Stack>
      </EditableSection>
    );

  if (mode === Mode.Creating)
    return (
      <PaymentMethodForm
        submitButtonLabel="Continue"
        onCancel={
          paymentMethods.length === 0 && !defaultPaymentMethod
            ? undefined
            : () => {
                setMode(paymentMethods.length > 0 ? Mode.Selecting : Mode.Resting);
              }
        }
        onSave={async method => {
          if (!method) return;
          await handleContinue(method.id);
        }}
      />
    );

  if (mode === Mode.Editing)
    return (
      <PaymentMethodForm
        submitButtonLabel="Continue"
        confirmCVV={paymentError === CVV_ERROR}
        onCancel={() => {
          if (paymentError) setPaymentError(null);
          setMode(Mode.Selecting);
        }}
        onSave={async () => {
          if (!editingPaymentMethodId) return;
          await handleContinue(editingPaymentMethodId);
          if (paymentError) setPaymentError(null);
          setEditingPaymentMethodId(null);
        }}
        method={paymentMethods.find(method => method.id === editingPaymentMethodId)}
      />
    );

  return (
    <div className="AdyenPaymentMethodSelector">
      <Stack space={6}>
        <Stack space={4}>
          {paymentMethods.map(method => (
            <PaymentMethodOption
              key={method.id}
              method={method}
              isSelected={method.id === selectedPaymentMethodId}
              onEdit={() => {
                setEditingPaymentMethodId(method.id);
                setMode(Mode.Editing);
              }}
              onSelect={() => setSelectedPaymentMethodId(method.id)}
            />
          ))}
        </Stack>
        <div>
          <ControlLink
            className="AdyenPaymentMethodSelector__add-button"
            onClick={() => {
              setMode(Mode.Creating);
            }}
          >
            Add New Payment Method
          </ControlLink>
        </div>
        <FormButtonsLayout>
          {defaultPaymentMethodId && (
            <Button
              data-test-id="adyen-payment-method-selector--cancel"
              secondary
              onClick={() => {
                setMode(Mode.Resting);
              }}
            >
              Cancel
            </Button>
          )}
          <Button
            data-test-id="adyen-payment-method-selector--continue"
            disabled={!selectedPaymentMethodId}
            onClick={() => {
              if (!selectedPaymentMethodId) return;
              setMode(Mode.Saving);
              handleContinue(selectedPaymentMethodId);
            }}
          >
            Continue
          </Button>
        </FormButtonsLayout>
      </Stack>

      {mode === Mode.Saving && (
        <Overlay>
          <LoadingBalls />

          <Text treatment="eyebrow">Saving</Text>
        </Overlay>
      )}
    </div>
  );
};
